import React, { useState, ChangeEvent, FormEvent, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dialog, DialogContent } from "@mui/material";
import { FaInstagram, FaUser } from "react-icons/fa";
import { RiGlobalLine } from "react-icons/ri";
import { FaCircleCheck } from "react-icons/fa6";
import { IoCloseCircle } from "react-icons/io5";
import { RootState } from "../redux/store";
import { submitTestimonial } from "../redux/actions/testimonialActions";
import InputField from "../components/input/InputField";
import BlueButton from "../components/buttons/BlueButton";

interface FormDataType {
    fullName: string;
    jobTitle: string;
    institutionName: string;
    testimonialText: string;
    instagram: string;
    website: string;
    imageUrl: File | null;
}

const ShareExperiencePage: React.FC = () => {
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [photoPreview, setPhotoPreview] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const dispatch = useDispatch();
    const { loading, success, error } = useSelector((state: RootState) => state.Testimonial);

    const [formData, setFormData] = useState<FormDataType>({
        fullName: "",
        jobTitle: "",
        institutionName: "",
        testimonialText: "",
        instagram: "",
        website: "",
        imageUrl: null,
    });

    useEffect(() => {
        if (success) {
            setIsDialogOpen(true);
        }
    }, [success]);

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handlePhotoUpload = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setFormData((prev) => ({ ...prev, imageUrl: file }));

            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const form = new FormData();
        form.append("fullName", formData.fullName);
        form.append("jobTitle", formData.jobTitle);
        form.append("institutionName", formData.institutionName);
        form.append("testimonialText", formData.testimonialText);

        const socialLinks: { platform: string; url: string }[] = [];
        if (formData.instagram.trim()) {
            socialLinks.push({ platform: "instagram", url: formData.instagram.trim() });
        }
        if (formData.website.trim()) {
            socialLinks.push({ platform: "website", url: formData.website.trim() });
        }
        if (socialLinks.length > 0) {
            form.append("socialLinks", JSON.stringify(socialLinks));
        }

        if (formData.imageUrl) {
            form.append("imageUrl", formData.imageUrl);
        }

        dispatch<any>(submitTestimonial(form));
    };

    const handleDialogClose = () => {
        setIsDialogOpen(false);
        setFormData({
            fullName: "",
            jobTitle: "",
            institutionName: "",
            testimonialText: "",
            instagram: "",
            website: "",
            imageUrl: null,
        });
        setPhotoPreview(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    return (
        <div className="min-h-[70vh] flex items-center justify-center py-2 ">
            <div className="w-full max-w-lg bg-white rounded-2xl shadow-lg p-8">
                <div className="mb-6 text-center">
                    <h1 className="text-2xl font-bold text-gray-800">Share Your Comicfy Experience</h1>
                    <p className="mt-1 text-gray-500 text-sm">
                        We'd love to hear how Comicfy has impacted your classroom!
                    </p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="flex flex-col items-center mb-4">
                        <label className="block text-sm font-medium text-gray-500 mb-1">Profile Photo</label>
                        <div className="relative mb-2">
                            <label htmlFor="photoInput">
                                <div className="w-20 h-20 rounded-full bg-gray-100 border-2 border-gray-200 flex items-center justify-center cursor-pointer overflow-hidden group">
                                    {photoPreview ? (
                                        <>
                                            <img src={photoPreview} alt="Profile" className="w-full h-full object-cover" />
                                            <button
                                                type="button"
                                                className="absolute top-0 right-0 text-gray-500 bg-white rounded-full p-0.5 m-0.5 shadow group-hover:opacity-100 opacity-80"
                                                style={{ fontSize: 22 }}
                                                onClick={e => { e.preventDefault(); setPhotoPreview(null); setFormData(prev => ({ ...prev, imageUrl: null })); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                                                tabIndex={-1}
                                            >
                                                <IoCloseCircle />
                                            </button>
                                        </>
                                    ) : (
                                        <FaUser className="text-gray-400" style={{ fontSize: 40 }} />
                                    )}
                                </div>
                            </label>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handlePhotoUpload}
                                style={{ display: "none" }}
                                id="photoInput"
                                ref={fileInputRef}
                            />
                        </div>
                        <span className="text-xs text-gray-500">Tap the circle to upload a photo</span>
                    </div>
                    <InputField
                        label="Full Name"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        placeholder="Jane Smith"
                        type="text"
                        required
                    />
                    <InputField
                        label="Job Title"
                        name="jobTitle"
                        value={formData.jobTitle}
                        onChange={handleChange}
                        placeholder="5th Grade Teacher"
                        type="text"
                        required
                    />
                    <InputField
                        label="Institution Name"
                        name="institutionName"
                        value={formData.institutionName}
                        onChange={handleChange}
                        placeholder="Lincoln Elementary"
                        type="text"
                        required
                    />
                    <div>
                        <label className="block mb-2 text-sm font-medium text-gray-500">Your Testimonial</label>
                        <textarea
                            name="testimonialText"
                            rows={5}
                            value={formData.testimonialText}
                            onChange={handleChange}
                            placeholder="Share how Comicfy has impacted your classroom..."
                            maxLength={500}
                            required
                            className="block w-full p-2.5 text-sm text-gray-900 rounded-lg bg-[#F6F6F6] focus:outline-none focus:ring focus:border-blue-100 border border-gray-200"
                        />
                        <div className="text-xs text-gray-400 text-right mt-1">{formData.testimonialText.length}/500</div>
                    </div>
                    <div>
                        <label className="block mb-2 text-sm font-medium text-gray-500">Social Links (Optional)</label>
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-gray-400"><FaInstagram style={{ fontSize: 20 }} /></span>
                            <div className="w-full">
                                <InputField
                                    label=""
                                    name="instagram"
                                    value={formData.instagram}
                                    onChange={handleChange}
                                    placeholder="Instagram username"
                                    type="text"
                                    required={false}
                                />
                            </div>
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-gray-400"><RiGlobalLine style={{ fontSize: 20 }} /></span>
                            <div className="w-full">
                                <InputField
                                    label=""
                                    name="website"
                                    value={formData.website}
                                    onChange={handleChange}
                                    placeholder="https://yourwebsite.com"
                                    type="url"
                                    required={false}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-center">
                        <BlueButton
                            label={loading ? "Submitting..." : "Submit Testimonial"}
                            width="100%"
                            height="38px"
                            className="text-sm"
                            disabled={loading}
                        />
                    </div>
                    {error && <p className="text-red-600 mt-2 text-sm text-center">{error}</p>}
                </form>
                <Dialog
                    open={isDialogOpen}
                    onClose={handleDialogClose}
                    PaperProps={{
                        sx: {
                            borderRadius: "1.5rem",
                            maxWidth: 400,
                            margin: "auto",
                            boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)"
                        }
                    }}
                >
                    <DialogContent className="flex flex-col items-center p-8 gap-4 bg-white text-black">
                        <div className="w-24 h-24 rounded-full bg-blue-50 flex items-center justify-center">
                            <FaCircleCheck size={50} className="text-[#007bff]" />
                        </div>
                        <div className="text-center space-y-2">
                            <h3 className="text-lg font-semibold text-gray-900">
                                Thank you! Your testimonial has been submitted.
                            </h3>
                            <p className="text-gray-600 text-sm">
                                We appreciate your feedback.
                            </p>
                        </div>
                        <BlueButton
                            label="Close"
                            width="100%"
                            height="42px"
                            className="text-sm font-medium mt-2"
                            onClick={handleDialogClose}
                        />
                    </DialogContent>
                </Dialog>
            </div>
        </div>
    );
};

export default ShareExperiencePage;
